/**
 * ScribeEvent
 *
 * This module is responsible for warehousing Scribe Event Data.
 *
 * @module    scribe_event
 * @author    Brian LaMattina <blamattina@wayfair.com>
 * @copyright 2016 Wayfair, LLC - All rights reserved
 */
import _ from 'underscore';
import SCRIBE_EVENTS from './scribe_event_constants';

/**
 * Construct a Scribe Event
 *
 * @class
 * @param {Object} data Event Data
 * @param {String} data.eventType Type of scribe event
 */
class ScribeEvent {
  constructor(data, transactionID, storeID) {
    let resultData = data;
    const error = this.constructor.validateEventData(resultData);
    if (error) {
      throw new Error('Invalid Scribe Event: ' + error);
    }
    if (resultData.eventType === SCRIBE_EVENTS.IMPRESSION) {
      // strip out the eventType; we don't need it for ImpressionEvents
      resultData = _.omit(resultData, (value, key) => {
        return (
          value === '' || typeof value === 'undefined' || key === 'eventType'
        );
      });
      this.data = resultData;
    } else {
      // get other required data for other events
      this.data = {
        transactionId: transactionID,
        storeId: storeID,
        ...resultData,
        timestamp: _.now(),
      };
    }
  }

  static validateEventData(data) {
    if (!_.isObject(data)) {
      return 'event data is not an object';
    } else if (typeof data.eventType !== 'string') {
      return 'event data must have an "eventType" property';
    } else if (data.eventType === SCRIBE_EVENTS.IMPRESSION) {
      if (typeof data.v !== 'string') {
        return 'impression event data must have a string property: "v"';
      }
    } else if (data.eventType === SCRIBE_EVENTS.ADMIN_DPN_CALLER_IDENTITY) {
      // If the event contains the required fields, send the event to the scribe client
      if (!data.customerPhoneNumber || !data.dpnCallSessionId || !data.dpn) {
        return 'required fields for AdminDpnCallerIdentityEvent (customerPhoneNumber, dpnCallSessionId, and dpn) are missing';
      }
    } else if (data.eventType === SCRIBE_EVENTS.CONTACT_US) {
      // If the event contains the required fields, send the event to the scribe client
      if (!data.triggerAction) {
        return 'required field for ContactUsEvent (triggerAction) is missing';
      }
    } else if (data.eventType === SCRIBE_EVENTS.SANDBOX) {
      // If the event contains the required field (name), send the event to the scribe client
      if (!data.name) {
        return 'required field for SandboxEvent (name) is missing';
      }
    }

    return false;
  }

  /**
   * Return the event's unix timestamp
   *
   * @return {Number} unix timestamp
   */
  getTimestamp() {
    return this.data.timestamp;
  }

  /**
   * Serialize a ScribeEvent to JSON
   *
   * @return {Object}
   */
  toJSON() {
    return _.clone(this.data);
  }
}

export default ScribeEvent;
